import {parseJwt} from '../../logics/parseJwt';
import {hel_textchat_notify_message, hel_toast_notify_message} from '../../logics/shared';
import {GetIHeliNetwork} from '../../singleton_registry';

import type {ChannelSession} from '../../types/network';
import type {NotifyCollectionScope, NotifyEventMap} from '../../types/notify';

export class CApiNotification {
  static readonly #callbackMap: {
    [Scope in NotifyCollectionScope]?: {
      [K in keyof NotifyEventMap]?: ((params: NotifyEventMap[K]) => void)[];
    };
  } = {};

  static addEventListener<K extends keyof NotifyEventMap>(
    scope: NotifyCollectionScope,
    actionType: K,
    callback: (params: NotifyEventMap[K]) => void
  ) {
    CApiNotification.#callbackMap[scope] ??= {};
    CApiNotification.#callbackMap[scope][actionType] ??= [];
    CApiNotification.#callbackMap[scope][actionType].push(callback);
  }

  static apiNotificationReciever = async <K extends keyof NotifyEventMap>(
    scope: NotifyCollectionScope,
    actionType: K,
    data: NotifyEventMap[K]
  ): Promise<void> => {
    if ('FilterBy' in data) {
      switch (data.FilterBy) {
        case 'PositionChannelCode':
          if (
            data.FilterBy in data &&
            data.PositionChannelCode !==
              GetIHeliNetwork()?.getCurrentPositionChannel()?.getChannelId()
          ) {
            // 別の座標チャンネル向けの通知なら
            return;
          }
          break;

        case 'ChannelSessionCode': {
          if (!(data.FilterBy in data)) {
            break;
          }
          const result = await heliport.v3.api.channelSessionApi.getChannelSessionJwt();
          if (!result.data) {
            throw new Error(
              'heliport.v3.api.channelSessionApi.getChannelSessionJwt() returns null'
            );
          }
          // NOTE: CHeliNetwork.getChannelSessionCodeByPlayerId(null) は、座標チャンネル入室中にしか使えない
          if (
            data[data.FilterBy] !== (parseJwt(result.data) as ChannelSession).channel_session_code
          ) {
            // 別のタブ向けの通知なら
            return;
          }
          break;
        }
        case 'SpatiumCode,WorldCode': {
          if (!('SpatiumCode' in data) || !('WorldCode' in data)) {
            break;
          }
          const detail = GetIHeliNetwork()?.getCurrentPositionChannel()?.getPositionChannelDetail();
          if (!detail) {
            break;
          }
          if (data.SpatiumCode !== detail.spatium_code || data.WorldCode !== detail.world_code) {
            // 別のワールド向けの通知なら
            return;
          }
          break;
        }
      }
    }

    switch (data.ActionType) {
      case 'ToastMessage':
        hel_toast_notify_message(data.Message);
        break;

      case 'SystemMessage': {
        // TODO: サーバー側で修正頂くまでの暫定対応
        if (typeof data.CreatedAt === 'object') {
          data.CreatedAt =
            '0000-00-00T' +
            new Date(data.CreatedAt?.seconds * 1000).toLocaleTimeString('Ja-JP', {
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'UTC'
            });
        }
        const jsonString = JSON.stringify(data).replace(/'/g, '\\"');
        hel_textchat_notify_message(jsonString, true);
        break;
      }
    }
    CApiNotification.#dispatchEvent(scope, actionType, data);
  };

  static #dispatchEvent<K extends keyof NotifyEventMap>(
    scope: NotifyCollectionScope,
    actionType: K,
    data: NotifyEventMap[K]
  ) {
    const callbacks = CApiNotification.#callbackMap[scope]?.[actionType];
    if (!callbacks) {
      return;
    }
    for (const callback of callbacks) {
      callback(data);
    }
  }
}
